import Vue from "vue";
import VueRouter from "vue-router";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";

// ============ ENERGY & INFRASTRUCTURE SERVICES ============
// - Business Operations
// - Market Initiatives & Partnerships
// - Infrastructure Products & Services

// ============ CORPORATE PLANNING, RISK, ADVISORY SERVICES ============

// ============ FINANCE ============
// - Regulatory Affairs
import FinanceRegulatoryRateApplication from "../views/Applications/Finance/Regulatory/RateApplication.vue";
// - Strategic Sourcing & Logistics

// ============ OPERATIONS, DISTRIBUTION, GENERATION ============
// - System Operations & Grid Automation
// - Distribution Construction & Maintenance
// - Generation: Development & Operations
// - Distribution Engineering & Asset Management
import OperationsDistributionSwitchOrders from "../views/Applications/Operations/Distribution/SwitchOrders.vue";
// - Distribution Program Delivery
// - Generation: Energy & Operations

// ============ CUSTOMER ============
// - Customer Service
import CustomerServiceCallAnalysis from "../views/Applications/Customer/Service/CallAnalysis.vue";
import CustomerServiceSmartSearch from "../views/Applications/Customer/Service/SmartSearch.vue";
// - Communications & Public Affairs
import CustomerCommunicationsContentManager from "../views/Applications/Customer/Communications/ContentManager.vue";
// - Energy Transition Customer Strategy & Innovation

// ============ PEOPLE AND CULTURE ============
// - People Systems, Technology & Analytics
// - Change and Organization Development
// - Human Resources Service & Labour Relations
// - Health, Safety, Environment

// ============ INFORMATION & TECHNOLOGY ============
// - Enterprise Applications
// - Cybersecurity & IT Infrastructure
// - Enterprise Architecture
// - IT Planning, Programs & Grid Technology

// ============ GENERAL COUNSEL ============

// ============ SKUNKWORKS (EXPERIMENTAL) ============
import SkunkworksAgentAssist from "../views/Applications/Skunkworks/AgentAssist.vue";
import SkunkworksArtificialAgent from "../views/Applications/Skunkworks/ArtificialAgent.vue";
import SkunkworksBillPrediction from "../views/Applications/Skunkworks/BillPrediction.vue";
import SkunkworksChatAssistant from "../views/Applications/Skunkworks/ChatAssistant.vue";
import SkunkworksDamageAssessment from "../views/Applications/Skunkworks/DamageAssessment.vue";
import SkunkworksForestry from "../views/Applications/Skunkworks/Forestry.vue";
import SkunkworksImagen from "../views/Applications/Skunkworks/Imagen.vue";
import SkunkworksMusic from "../views/Applications/Skunkworks/Music.vue";
import SkunkworksNameplateAutomation from "../views/Applications/Skunkworks/NameplateAutomation.vue";
import SkunkworksPodcasts from "../views/Applications/Skunkworks/Podcasts.vue";
import SkunkworksWorkforceAutomation from "../views/Applications/Skunkworks/WorkforceAutomation.vue";

// ============ COMMON PAGES ============
import News from "../views/News.vue";
import Landing from "../views/Landing.vue";
import Login from "../views/Login.vue";
import Team from "../views/Team.vue";
import CreateRequest from "../views/CreateRequest.vue";
import RateExplorer from "../views/RateExplorer.vue";
import Account from "../views/Account.vue";

// ============ ERROR PAGES ============
import Forbidden from "../views/errors/Forbidden.vue";
import NotFound from "../views/errors/NotFound.vue";
import Unknown from "../views/errors/Unknown.vue";

// ============ PRIVATE PAGES ============
import Dashboard from "../views/Dashboard.vue";

// ============ ADMIN PAGES ============
// Admin imports with fallbacks for backwards compatibility
let UserManagement, UserMigration, CallAnalysisAdmin, AgentAssistAdmin, SmartSearchAdmin, ArtificialAgentAdmin, ToolAdmin, UseCaseRequestsAdmin
import CallAnalysisEvaluation from "../views/Admin/CallAnalysisEvaluation.vue";
import CallAnalysisPipeline from "../views/Admin/CallAnalysisPipeline.vue";

try {
  UserManagement = require("../views/Admin/UserManagement.vue").default
} catch (error) {
  console.warn('UserManagement component not available')
}

try {
  UseCaseRequestsAdmin = require("../views/Admin/UseCaseRequestsAdmin.vue").default
} catch (error) {
  console.warn('UseCaseRequestsAdmin component not available')
}

try {
  UserMigration = require("../views/Admin/UserMigration.vue").default
} catch (error) {
  console.warn('UserMigration component not available')
}

try {
  CallAnalysisAdmin = require("../views/Admin/CallAnalysisAdmin.vue").default
} catch (error) {
  console.warn('CallAnalysisAdmin component not available')
}

try {
  AgentAssistAdmin = require("../views/Admin/AgentAssistAdmin.vue").default
} catch (error) {
  console.warn('AgentAssistAdmin component not available')
}

try {
  SmartSearchAdmin = require("../views/Admin/SmartSearchAdmin.vue").default
} catch (error) {
  console.warn('SmartSearchAdmin component not available')
}

try {
  ArtificialAgentAdmin = require("../views/Admin/ArtificialAgentAdmin.vue").default
} catch (error) {
  console.warn('ArtificialAgentAdmin component not available')
}

try {
  ToolAdmin = require("../views/Admin/ToolAdmin.vue").default
} catch (error) {
  console.warn('ToolAdmin component not available')
}

Vue.use(VueRouter);

// Authentication initialization promise
let authInitialized = false;
const authInitPromise = new Promise((resolve) => {
  const auth = getAuth();
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (!authInitialized) {
      authInitialized = true;
      unsubscribe();
      resolve(user);
    }
  });
});

const routes = [
  // ============ COMMON ROUTES ============
  {
    path: "/",
    name: "Landing",
    component: Landing,
    meta: {
      scope: "public",
    },
  },
  {
    path: "/news",
    name: "News",
    component: News,
    meta: {
      requiresAuth: true,
      title: "News"
    }
  },
  {
    path: "/intranet",
    name: "Intranet",
    component: () => import("../views/Intranet.vue"),
    meta: {
      requiresAuth: true,
      title: "Intranet"
    }
  },
  {
    path: "/team",
    name: "Team",
    component: Team,
    meta: {
      scope: "private",
      roles: ["Employee"],
    },
  },
  {
    path: "/account",
    name: "Account",
    component: Account,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: {
      scope: "private",
      roles: ["Employee"],
    },
  },
  {
    path: "/explorer",
    name: "RateExplorer",
    component: RateExplorer,
    meta: {
      scope: "private",
      roles: ["Employee"],
    },
  },
  {
          path: "/create-request",
    name: "CreateRequest",
    component: CreateRequest,
  },

  // ============ ADMIN ROUTES ============
  ...(UserManagement ? [{
    path: "/admin/user-management",
    name: "UserManagement",
    component: UserManagement,
    meta: {
      scope: "private",
      roles: ["Manager", "Admin"],
    },
  }] : []),
  ...(UseCaseRequestsAdmin ? [{
    path: "/admin/use-case-requests",
    name: "UseCaseRequestsAdmin",
    component: UseCaseRequestsAdmin,
    meta: {
      scope: "private",
      roles: ["Admin"],
    },
  }] : []),
  ...(UserMigration ? [{
    path: "/admin/user-migration",
    name: "UserMigration",
    component: UserMigration,
    meta: {
      scope: "private",
      roles: ["Admin"],
    },
  }] : []),
  ...(ToolAdmin ? [{
    path: "/admin/tools",
    name: "ToolAdmin",
    component: ToolAdmin,
    meta: {
      scope: "private",
      roles: ["Admin"],
    },
  }] : []),
  {
    path: "/admin/evaluation",
    name: "CallAnalysisEvaluation",
    component: CallAnalysisEvaluation,
    meta: {
      scope: "private",
      roles: ["Admin"],
    },
  },
  {
    path: "/admin/call-analysis-pipeline",
    name: "CallAnalysisPipeline",
    component: CallAnalysisPipeline,
    meta: {
      scope: "private",
      roles: ["Admin"],
    },
  },
  ...(CallAnalysisAdmin ? [{
    path: "/admin/call-analysis",
    name: "CallAnalysisAdmin",
    component: CallAnalysisAdmin,
    meta: {
      scope: "private",
      requiresToolManagement: "CustomerServiceCallAnalysis",
    },
  }] : []),
  ...(AgentAssistAdmin ? [{
    path: "/admin/agent-assist",
    name: "AgentAssistAdmin",
    component: AgentAssistAdmin,
    meta: {
      scope: "private",
      requiresToolManagement: "SkunkworksAgentAssist",
    },
  }] : []),
  ...(SmartSearchAdmin ? [{
    path: "/admin/smart-search",
    name: "SmartSearchAdmin",
    component: SmartSearchAdmin,
    meta: {
      scope: "private",
      requiresToolManagement: "CustomerServiceSmartSearch",
    },
  }] : []),
  ...(ArtificialAgentAdmin ? [{
    path: "/admin/artificial-agent",
    name: "ArtificialAgentAdmin",
    component: ArtificialAgentAdmin,
    meta: {
      scope: "private",
      requiresToolManagement: "SkunkworksArtificialAgent",
    },
  }] : []),

  // ============ ENERGY & INFRASTRUCTURE SERVICES ============
  // Business Operations
  // Market Initiatives & Partnerships
  // Infrastructure Products & Services

  // ============ CORPORATE PLANNING, RISK, ADVISORY SERVICES ============

  // ============ FINANCE ============
  // Regulatory Affairs
  {
    path: "/finance/regulatory/rate-application",
    name: "FinanceRegulatoryRateApplication",
    component: FinanceRegulatoryRateApplication,
    meta: {
      scope: "private",
      roles: ["Finance", "Regulatory"],
    },
  },
  // Strategic Sourcing & Logistics

  // ============ OPERATIONS, DISTRIBUTION, GENERATION ============
  // System Operations & Grid Automation
  // Distribution Construction & Maintenance
  // Generation: Development & Operations
  // Distribution Engineering & Asset Management
  {
    path: "/operations/distribution/switch-orders",
    name: "OperationsDistributionSwitchOrders",
    component: OperationsDistributionSwitchOrders,
    meta: {
      scope: "private",
      tool: "OperationsDistributionSwitchOrders",
    },
  },
  // Distribution Program Delivery
  // Generation: Energy & Operations

  // ============ CUSTOMER ============
  // Customer Service
  {
    path: "/customer/service/call-analysis",
    name: "CustomerServiceCallAnalysis",
    component: CustomerServiceCallAnalysis,
    meta: {
      scope: "private",
      tool: "CustomerServiceCallAnalysis",
    },
  },
  {
    path: "/customer/service/call-analysis/:id",
    name: "CustomerServiceCallAnalysisDetail",
    component: CustomerServiceCallAnalysis,
    meta: {
      scope: "private",
      tool: "CustomerServiceCallAnalysis",
    },
  },
  {
    path: "/customer/service/smart-search",
    name: "CustomerServiceSmartSearch",
    component: CustomerServiceSmartSearch,
    meta: {
      scope: "private",
      tool: "CustomerServiceSmartSearch",
    },
  },
  // Communications & Public Affairs
  {
    path: "/customer/communications/content-manager",
    name: "CustomerCommunicationsContentManager",
    component: CustomerCommunicationsContentManager,
    meta: {
      scope: "private",
      tool: "CustomerCommunicationsContentManager",
    },
  },
  // Energy Transition Customer Strategy & Innovation

  // ============ PEOPLE AND CULTURE ============
  // People Systems, Technology & Analytics
  // Change and Organization Development
  // Human Resources Service & Labour Relations
  // Health, Safety, Environment

  // ============ INFORMATION & TECHNOLOGY ============
  // Enterprise Applications
  // Cybersecurity & IT Infrastructure
  // Enterprise Architecture
  // IT Planning, Programs & Grid Technology

  // ============ GENERAL COUNSEL ============

  // ============ SKUNKWORKS (EXPERIMENTAL) ============
  {
    path: "/skunkworks/agent-assist",
    name: "SkunkworksAgentAssist",
    component: SkunkworksAgentAssist,
    meta: {
      scope: "private",
      tool: "SkunkworksAgentAssist",
    },
  },
  {
    path: "/skunkworks/artificial-agent",
    name: "SkunkworksArtificialAgent",
    component: SkunkworksArtificialAgent,
    meta: {
      scope: "private",
      tool: "SkunkworksArtificialAgent",
    },
  },
  {
    path: "/skunkworks/bill-prediction",
    name: "SkunkworksBillPrediction",
    component: SkunkworksBillPrediction,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/chat-assistant",
    name: "SkunkworksChatAssistant",
    component: SkunkworksChatAssistant,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/damage-assessment",
    name: "SkunkworksDamageAssessment",
    component: SkunkworksDamageAssessment,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/forestry",
    name: "SkunkworksForestry",
    component: SkunkworksForestry,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/imagen",
    name: "SkunkworksImagen",
    component: SkunkworksImagen,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/music",
    name: "SkunkworksMusic",
    component: SkunkworksMusic,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/nameplate-automation",
    name: "SkunkworksNameplateAutomation",
    component: SkunkworksNameplateAutomation,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/podcasts",
    name: "SkunkworksPodcasts",
    component: SkunkworksPodcasts,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },
  {
    path: "/skunkworks/workforce-automation",
    name: "SkunkworksWorkforceAutomation",
    component: SkunkworksWorkforceAutomation,
    meta: {
      scope: "private",
      roles: ["Pioneer"],
    },
  },

  // ============ ERROR ROUTES ============
  {
    path: "/403",
    name: "Forbidden",
    component: Forbidden,
    meta: {
      scope: "public",
    },
  },
  {
    path: "/520",
    name: "Unknown",
    component: Unknown,
    meta: {
      scope: "public",
    },
  },
  {
    path: "/uninvited-user",
    name: "UninvitedUser",
    component: () => import("../views/errors/UninvitedUser.vue"),
    meta: {
      scope: "public",
    },
  },
  {
    path: "*",
    name: "NotFound",
    component: NotFound,
    meta: {
      scope: "public",
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeResolve(async (to, from, next) => {
  // ---------------------- Public ----------------------
  if (to.meta.scope == "public") {
    next();
    return;
  }

  // Wait for authentication to be initialized
  await authInitPromise;
  
  let user = getAuth().currentUser;
  // ---------------------- Not Public ----------------------
  if (user) {
    // ---------------------- Logged In ----------------------
    try {
      const db = getFirestore();
      let userData = null;
      
      // Try email-based document first
      try {
        const emailUserRef = doc(db, "users", user.email);
        const emailUserDoc = await getDoc(emailUserRef);
        if (emailUserDoc.exists() && emailUserDoc.data().uid) {
          userData = emailUserDoc.data();
        }
      } catch (emailError) {
        console.warn("Could not access email-based user document:", emailError);
      }
      
      // Fallback to UID-based document if email-based failed
      if (!userData) {
        try {
          const uidUserRef = doc(db, "users", user.uid);
          const uidUserDoc = await getDoc(uidUserRef);
          if (uidUserDoc.exists()) {
            userData = uidUserDoc.data();
          }
        } catch (uidError) {
          console.warn("Could not access UID-based user document:", uidError);
        }
      }
      
      if (userData && userData.role && userData.role !== '') {
        // User exists and is active
        if (to.meta.tool) {
          // Tool-specific access check
          const toolAccess = userData.tool_permissions?.[to.meta.tool]?.access;
          
          // Import role checking functions
          const { userHasRoleLevel } = await import('@/config/toolsConfig');
          
          if (userHasRoleLevel(userData, "Admin") || toolAccess) {
            next();
          } else {
            next({ name: "Forbidden" });
          }
        } else if (to.meta.requiresUserManagement) {
          // User management access check - Admin or Tool Manager
          const { userHasRoleLevel } = await import('@/config/toolsConfig');
          const isAdmin = userHasRoleLevel(userData, "Admin");
          const isToolManager = Object.values(userData.tool_permissions || {}).some(
            permission => permission.manager === true
          );
          if (isAdmin || isToolManager) {
            next();
          } else {
            next({ name: "Forbidden" });
          }
        } else if (to.meta.requiresToolManagement) {
          // Tool-specific management access check (backwards compatible)
          const { userHasRoleLevel } = await import('@/config/toolsConfig');
          const isAdmin = userHasRoleLevel(userData, "Admin");
          const toolId = to.meta.requiresToolManagement;
          const isToolManager = userData.tool_permissions && 
                               userData.tool_permissions[toolId] && 
                               userData.tool_permissions[toolId].manager === true;
          
          if (isAdmin || isToolManager) {
            next();
          } else {
            next({ name: "Forbidden" });
          }
        } else if (to.meta.roles) {
          // Role-based access check (for general routes) - Updated for hierarchy
          console.log("User Data:", { role: userData.role, roles: userData.roles, email: userData.email });
          console.log("Required roles:", to.meta.roles);
          
          // Import role checking functions
          const { userHasRoleLevel } = await import('@/config/toolsConfig');
          
          const hasAdminLevel = userHasRoleLevel(userData, "Admin");
          console.log("Has Admin level:", hasAdminLevel);
          
          if (hasAdminLevel) {
            console.log("Admin access granted");
            next();
          } else if (to.meta.roles.some(role => userHasRoleLevel(userData, role))) {
            console.log("Role-based access granted");
            next();
                  } else {
          console.log("Access denied - insufficient role");
          next({ name: "Forbidden" });
        }
        } else {
          // Route does not have roles or tool requirements defined
          next();
        }
      } else {
        // User does not exist or is inactive
        // Only redirect if we're not already on Landing to prevent navigation loops
        if (to.name !== "Landing") {
          next({ name: "Landing" });
        } else {
          next();
        }
      }
    } catch (error) {
      // Error getting user document
      console.error("Error getting user document:", error);
      // Only redirect if we're not already on Unknown to prevent navigation loops
      if (to.name !== "Unknown") {
        next({ name: "Unknown" });
      } else {
        next();
      }
    }
  } else {
    // ---------------------- Not Logged In ----------------------
    // Only redirect if we're not already on Landing to prevent navigation loops
    if (to.name !== "Landing") {
      next({ name: "Landing" });
    } else {
      next();
    }
  }
});

export default router;
